import axios, { AxiosAdapter, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { services } from 'services/base';
import {
  GetCordovaHttpPluginAdapterService,
  IsCordovaHttpPluginAvailableService,
  IsCordovaInAppBrowserAvailableService,
} from 'types';
import { logger } from 'utils';
import { isInAppBrowserAvailable } from 'utils/cordova';

const SERVICE_PREFIX = 'appFrame.cordova';
const CONTENT_TYPE_HEADER_KEY = 'Content-Type';

const filterMapStringValues = (orig: Record<string, any>) => {
  const result: Record<string, string> = {};
  Object.keys(orig).forEach((key) => {
    if (typeof orig[key] === 'string') {
      result[key] = orig[key];
    }
  });
  if (result[CONTENT_TYPE_HEADER_KEY] !== 'application/json') {
    result[CONTENT_TYPE_HEADER_KEY] = 'application/json';
  }
  return result;
};

const isCordovaHttpPluginAvailable = () => !!window.cordova?.plugin?.http;

const initCordovaServices = () => {
  const { registerService } = services;

  registerService<IsCordovaInAppBrowserAvailableService>(
    `${SERVICE_PREFIX}.isInAppBrowserAvailable`,
    isInAppBrowserAvailable,
  );

  registerService<IsCordovaHttpPluginAvailableService>(
    `${SERVICE_PREFIX}.isHttpPluginAvailable`,
    isCordovaHttpPluginAvailable,
  );

  const cordovaHttpPluginAdapter: AxiosAdapter = (config: AxiosRequestConfig) => {
    const pluginOptions = {
      method: config.method,
      data: typeof config.data === 'string' ? JSON.parse(config.data) : config.data || {},
      params: config.params,
      responseType: config.responseType || 'json',
      timeout: config.timeout,
      headers: filterMapStringValues(config.headers || {}),
    };

    logger.debug('Call cordova http plugin', config.url, pluginOptions);
    return new Promise<AxiosResponse>((resolve, reject) => {
      window.cordova!.plugin!.http.sendRequest(
        config.url,
        pluginOptions,
        (result: any) => {
          logger.debug('Cordova http plugin request success', config.url, result);
          resolve({
            ...result,
            config: {
              headers: result.headers,
              url: result.url,
              ...config,
            },
          });
        },
        (err: any) => {
          logger.error('Cordova http plugin request failed', config.url, err, pluginOptions);
          const axiosErr: AxiosError = {
            ...err,
            message: err.error,
            code: err.status,
            config,
          };
          reject(axiosErr);
        },
      );
    });
  };

  registerService<GetCordovaHttpPluginAdapterService>(
    `${SERVICE_PREFIX}.getHttpPluginAdapter`,
    () => cordovaHttpPluginAdapter,
  );

  // setup if cordova-http-plugin installed to application
  if (isCordovaHttpPluginAvailable()) {
    window.cordova!.plugin.http.clearCookies();
    window.cordova!.plugin.http.setDataSerializer('json');
    axios.defaults.adapter = cordovaHttpPluginAdapter;
  }
};

export default initCordovaServices;
